import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import '../styles/Page.css';
import '../styles/ConfigureAdmins.css';

const hostURL = config.serverUrl;

// Password validation function
const validatePassword = (password) => {
  if (password.length < 8) {
    return { valid: false, error: 'Password must be at least 8 characters long' };
  }
  
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSymbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  
  if (!hasUppercase) {
    return { valid: false, error: 'Password must include at least one uppercase letter' };
  }
  
  if (!hasLowercase) {
    return { valid: false, error: 'Password must include at least one lowercase letter' };
  }
  
  if (!hasNumber && !hasSymbol) {
    return { valid: false, error: 'Password must include at least one number or symbol' };
  }
  
  return { valid: true };
};

function ConfigureAdmins() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dropdown menu state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const dropdownRef = useRef(null);
  
  // Edit email popup state
  const [showEditEmailPopup, setShowEditEmailPopup] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  
  // Change password popup state
  const [showChangePasswordPopup, setShowChangePasswordPopup] = useState(false);
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [confirmAdminPassword, setConfirmAdminPassword] = useState('');
  
  // Owner password verification popup state
  const [showOwnerPasswordPopup, setShowOwnerPasswordPopup] = useState(false);
  const [ownerPassword, setOwnerPassword] = useState('');
  const [verifiedOwnerPassword, setVerifiedOwnerPassword] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  
  // Delete confirmation popup state
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);

  // Redirect if not owner
  useEffect(() => {
    if (!currentUser || currentUser.access !== 'owner') {
      navigate('/employee-portal');
    }
  }, [currentUser, navigate]);

  // Fetch admins on mount
  useEffect(() => {
    fetchAdmins();
  }, []);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${hostURL}/api/admins`, {
        withCredentials: true
      });
      setAdmins(response.data.admins);
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newEmail || !newPassword) {
      setError('Email and password are required');
      return;
    }
    
    // Validate password strength
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setError(passwordValidation.error);
      return;
    }

    try {
      await axios.post(
        `${hostURL}/api/admins`,
        { email: newEmail, password: newPassword },
        { withCredentials: true }
      );

      setSuccess('Admin user created successfully!');
      setNewEmail('');
      setNewPassword('');
      setShowAddPopup(false);
      
      // Refresh the admin list
      fetchAdmins();
    } catch (err) {
      console.error('Error creating admin:', err);
      setError(err.response?.data?.error || 'Failed to create admin user');
    }
  };

  const handleCloseAddPopup = () => {
    setShowAddPopup(false);
    setNewEmail('');
    setNewPassword('');
    setError('');
  };
  
  // Toggle dropdown menu
  const toggleDropdown = (adminId) => {
    setOpenDropdownId(openDropdownId === adminId ? null : adminId);
  };
  
  // Handle edit email click
  const handleEditEmailClick = (admin) => {
    setEditingAdmin(admin);
    setEditEmail(admin.email);
    setOpenDropdownId(null);
    setPendingAction({ type: 'editEmail', admin });
    setShowOwnerPasswordPopup(true);
  };
  
  // Handle change password click
  const handleChangePasswordClick = (admin) => {
    setEditingAdmin(admin);
    setOpenDropdownId(null);
    setPendingAction({ type: 'changePassword', admin });
    setShowOwnerPasswordPopup(true);
  };
  
  // Handle delete click
  const handleDeleteClick = (admin) => {
    setAdminToDelete(admin);
    setOpenDropdownId(null);
    setShowDeleteConfirmPopup(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    setShowDeleteConfirmPopup(false);
    setPendingAction({ type: 'delete', admin: adminToDelete });
    setShowOwnerPasswordPopup(true);
  };
  
  // Handle owner password verification
  const handleOwnerPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!ownerPassword) {
      setError('Password is required');
      return;
    }
    
    try {
      // Verify owner password
      await axios.post(
        `${hostURL}/api/admins/verify-owner`,
        { password: ownerPassword },
        { withCredentials: true }
      );
      
      // Password verified, proceed with pending action
      setShowOwnerPasswordPopup(false);
      const verifiedPassword = ownerPassword;
      setVerifiedOwnerPassword(verifiedPassword);
      setOwnerPassword('');
      
      if (pendingAction.type === 'editEmail') {
        setShowEditEmailPopup(true);
      } else if (pendingAction.type === 'changePassword') {
        setShowChangePasswordPopup(true);
      } else if (pendingAction.type === 'delete') {
        await executeDelete(verifiedPassword);
      }
    } catch (err) {
      console.error('Error verifying password:', err);
      setError(err.response?.data?.error || 'Invalid password');
    }
  };
  
  // Execute edit email
  const handleEditEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!editEmail) {
      setError('Email is required');
      return;
    }
    
    try {
      await axios.put(
        `${hostURL}/api/admins/${editingAdmin.id}/email`,
        { newEmail: editEmail, ownerPassword: verifiedOwnerPassword },
        { withCredentials: true }
      );
      
      setSuccess('Admin email updated successfully!');
      setShowEditEmailPopup(false);
      setEditingAdmin(null);
      setEditEmail('');
      setVerifiedOwnerPassword('');
      setPendingAction(null);
      fetchAdmins();
    } catch (err) {
      console.error('Error updating email:', err);
      setError(err.response?.data?.error || 'Failed to update email');
    }
  };
  
  // Execute change password
  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!newAdminPassword || !confirmAdminPassword) {
      setError('Both password fields are required');
      return;
    }
    
    if (newAdminPassword !== confirmAdminPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate password strength
    const passwordValidation = validatePassword(newAdminPassword);
    if (!passwordValidation.valid) {
      setError(passwordValidation.error);
      return;
    }
    
    try {
      await axios.put(
        `${hostURL}/api/admins/${editingAdmin.id}/password`,
        { 
          newPassword: newAdminPassword, 
          confirmPassword: confirmAdminPassword,
          ownerPassword: verifiedOwnerPassword 
        },
        { withCredentials: true }
      );
      
      setSuccess('Admin password updated successfully!');
      setShowChangePasswordPopup(false);
      setEditingAdmin(null);
      setNewAdminPassword('');
      setConfirmAdminPassword('');
      setVerifiedOwnerPassword('');
      setPendingAction(null);
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.error || 'Failed to change password');
    }
  };
  
  // Execute delete
  const executeDelete = async (password) => {
    try {
      await axios.delete(
        `${hostURL}/api/admins/${pendingAction.admin.id}`,
        { 
          data: { ownerPassword: password },
          withCredentials: true 
        }
      );
      
      setSuccess('Admin deleted successfully!');
      setAdminToDelete(null);
      setVerifiedOwnerPassword('');
      setPendingAction(null);
      fetchAdmins();
    } catch (err) {
      console.error('Error deleting admin:', err);
      setError(err.response?.data?.error || 'Failed to delete admin');
    }
  };
  
  // Close all popups
  const closeAllPopups = () => {
    setShowEditEmailPopup(false);
    setShowChangePasswordPopup(false);
    setShowOwnerPasswordPopup(false);
    setShowDeleteConfirmPopup(false);
    setEditingAdmin(null);
    setAdminToDelete(null);
    setEditEmail('');
    setNewAdminPassword('');
    setConfirmAdminPassword('');
    setOwnerPassword('');
    setVerifiedOwnerPassword('');
    setError('');
    setPendingAction(null);
  };

  if (!currentUser || currentUser.access !== 'owner') {
    return null;
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Configure Admins</h1>
        <p>Manage administrative users</p>
      </div>

      <div className="page-content">
        {success && <div className="success-message">{success}</div>}
        
        {loading ? (
          <div className="loading">Loading admin users...</div>
        ) : (
          <div className="admin-grid">
            {/* Add Admin Card */}
            <div 
              className="admin-card add-admin-card"
              onClick={() => setShowAddPopup(true)}
            >
              <div className="add-admin-icon">+</div>
              <p>Add New Admin</p>
            </div>

            {/* Existing Admin Cards */}
            {admins.map((admin) => (
              <div key={admin.id} className="admin-card">
                <div className="admin-card-header">
                  <div className="admin-card-icon"><User size={32} color="#4dd0e1" /></div>
                  <div className="admin-menu-container" ref={openDropdownId === admin.id ? dropdownRef : null}>
                    <button 
                      className="admin-menu-button"
                      onClick={() => toggleDropdown(admin.id)}
                      aria-label="Admin options"
                    >
                      ⋮
                    </button>
                    {openDropdownId === admin.id && (
                      <div className="admin-dropdown">
                        <button 
                          className="dropdown-item"
                          onClick={() => handleEditEmailClick(admin)}
                        >
                          Edit Email
                        </button>
                        <button 
                          className="dropdown-item"
                          onClick={() => handleChangePasswordClick(admin)}
                        >
                          Change Password
                        </button>
                        <button 
                          className="dropdown-item delete"
                          onClick={() => handleDeleteClick(admin)}
                        >
                          Delete Admin
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <h3>{admin.email}</h3>
                <p className="admin-access">Access: {admin.access}</p>
                {admin.createdby && (
                  <p className="admin-created-by">Created by: {admin.createdby}</p>
                )}
              </div>
            ))}
          </div>
        )}

        <button 
          className="back-button" 
          onClick={() => navigate('/employee-portal')}
        >
          Back to Employee Portal
        </button>
      </div>

      {/* Add Admin Popup */}
      {showAddPopup && (
        <div className="popup-overlay" onClick={handleCloseAddPopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add New Admin</h2>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleAddAdmin}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter email"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                />
                <small style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Min 8 characters, must include: uppercase, lowercase, and (numbers or symbols)
                </small>
              </div>
              
              <div className="popup-buttons">
                <button type="button" onClick={handleCloseAddPopup} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="confirm-button">
                  Confirm
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Popup */}
      {showDeleteConfirmPopup && (
        <div className="popup-overlay" onClick={() => setShowDeleteConfirmPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Admin</h2>
            <p className="warning-text">
              Are you sure you want to delete <strong>{adminToDelete?.email}</strong>?
              <br />
              This action cannot be undone.
            </p>
            
            <div className="popup-buttons">
              <button 
                type="button" 
                onClick={() => setShowDeleteConfirmPopup(false)} 
                className="cancel-button"
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={handleDeleteConfirm} 
                className="delete-button"
              >
                Delete Admin
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Owner Password Verification Popup */}
      {showOwnerPasswordPopup && (
        <div className="popup-overlay" onClick={closeAllPopups}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h2>Verify Your Password</h2>
            <p className="info-text">Please enter your owner password to continue.</p>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleOwnerPasswordSubmit}>
              <div className="form-group">
                <label htmlFor="ownerPassword">Your Password</label>
                <input
                  type="password"
                  id="ownerPassword"
                  value={ownerPassword}
                  onChange={(e) => setOwnerPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoFocus
                />
              </div>
              
              <div className="popup-buttons">
                <button type="button" onClick={closeAllPopups} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="confirm-button">
                  Verify
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Email Popup */}
      {showEditEmailPopup && (
        <div className="popup-overlay" onClick={closeAllPopups}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Admin Email</h2>
            <p className="info-text">Editing: <strong>{editingAdmin?.email}</strong></p>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleEditEmailSubmit}>
              <div className="form-group">
                <label htmlFor="editEmail">New Email</label>
                <input
                  type="email"
                  id="editEmail"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="Enter new email"
                  required
                  autoFocus
                />
              </div>
              
              <div className="popup-buttons">
                <button type="button" onClick={closeAllPopups} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="confirm-button">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Change Password Popup */}
      {showChangePasswordPopup && (
        <div className="popup-overlay" onClick={closeAllPopups}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h2>Change Admin Password</h2>
            <p className="info-text">Changing password for: <strong>{editingAdmin?.email}</strong></p>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleChangePasswordSubmit}>
              <div className="form-group">
                <label htmlFor="newAdminPassword">New Password</label>
                <input
                  type="password"
                  id="newAdminPassword"
                  value={newAdminPassword}
                  onChange={(e) => setNewAdminPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  autoFocus
                />
                <small style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Min 8 characters, must include: uppercase, lowercase, and (numbers or symbols)
                </small>
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmAdminPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmAdminPassword"
                  value={confirmAdminPassword}
                  onChange={(e) => setConfirmAdminPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                />
              </div>
              
              <div className="popup-buttons">
                <button type="button" onClick={closeAllPopups} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="confirm-button">
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConfigureAdmins;
